/**
 * 🐟 **The four fish, what they are called and what they are worth** — D115.
 *
 * 🎯 **haugur, 2026-09-16:** *"i need fishes in the world, lets start with 4
 * types."* **He named all four and set every food value.**
 *
 * ## 🔴 THIS FILE USED TO SAY "A FISH IS DERIVED". IT IS NOT, AND HAS NOT BEEN
 * SINCE 2026-09-17.
 *
 * ⚠️ **The header that was here argued the case for a formula in three numbered
 * points, and every one of them was about COST** — no row, no message, no
 * server. **Not one claimed it made a better fish.**
 *
 * 🎯 **He watched the result and described it exactly:** *"its a xerox copy of a
 * picture of a cod floting in a sea that has no movement."*
 *
 * 👉 **A fish is a creature the server keeps now — D117.** Its mind is in
 * [`fishmind.ts`](fishmind.ts), its population in `server/src/shoal.ts`, and
 * its body is unchanged in `client/src/fishmesh.ts`.
 *
 * ## ⚠️ What this module is
 *
 * **The species table and nothing else.** No geometry, no behaviour, no
 * position — just the four names, the four food values and the four lengths, so
 * that the mind, the body and the cookbook all read one copy.
 */

/**
 * 🐟 **The four fish, and haugur named every one of them.**
 *
 * ⚠️ **ASCII-folded keys like every other key in this project** — `thorskur`,
 * not `þorskur` — the rule `moberg` and `gragryti` already follow. The Icelandic
 * spelling is what a player reads and lives in `nameIs`.
 */
export type FishKind = 'thorskur' | 'ufsi' | 'lax' | 'bleikja';

/** Where a fish will live. */
export type FishWater = 'sea' | 'river' | 'both';

export interface FishSpecies {
  readonly key: FishKind;
  readonly name: string;
  readonly nameIs: string;
  readonly water: FishWater;
  /**
   * 🍲 **What it is worth, in haugur's own unit of *food*** — D115.
   *
   * **One food is 12.5% of the bar raw and 25% cooked.** ⚠️ **His second
   * answer**: at 25% a unit a cooked cod filled the whole bar and a cooked
   * salmon overflowed it by half, and shown that arithmetic he halved it.
   * **A crowberry is 10%**, for scale.
   */
  readonly food: number;
  /**
   * How long it is, in metres.
   *
   * 🎯 **haugur set these as SHARES OF THE ÞORSKUR, 2026-09-17:** *"lets have
   * lax 35% larger than Þorskur and ufsi 80% of þorskur size, that way we get
   * diversity to the look of the fish schools."* 🔴 **The þorskur's 80 cm is
   * unchanged, deliberately** — he declined to reopen it: *"insted of messing
   * with that."* **So the open question about the cod's absolute size is
   * answered by being left alone.**
   *
   * ## ⚠️ What the real fish measure, so nobody "corrects" these later
   *
   * | | In Icelandic waters | Ours |
   * |---|---|---|
   * | þorskur | **55–90 cm** in the catch, 186 cm record | 0.80 |
   * | ufsi | **60–110 cm** in the catch, 132 cm record | 0.64 |
   * | lax | **71–76 cm** after two winters at sea | 1.08 |
   * | bleikja | **40–60 cm** sea-run in Iceland | 0.48 |
   *
   * 🔴 **SO OUR ORDER IS THE OPPOSITE OF THE OPEN SEA'S, AND THAT IS A DECISION
   * RATHER THAN AN ERROR.** Out at sea an ufsi is the biggest of the three and a
   * lax the smallest. Two things justify the flip and both were put to him:
   *
   * 1. 🐟 **In the SHALLOWS, where ours swim, the small ufsi is the correct
   *    fish.** Saithe of nought to two years live inshore among kelp and rock
   *    and move offshore as they grow — so a 64 cm ufsi a few metres off the
   *    beach is right, and the 110 cm ones are far out and deep.
   * 2. 🎮 **Size then tells a player what a fish is worth before they throw
   *    anything at it** — lax 3 food and biggest, ufsi 1 food and smallest.
   *    **That legibility is worth more than the taxonomy**, and it is the
   *    argument he was given before agreeing.
   *
   * ⚠️ **A 1.08 m lax is a trophy fish**, not an average one. He was told.
   *
   * 🔴 **±15% ON TOP OF THIS IS ALREADY DRAWN PER FISH** by the client, from
   * each fish's own id — so a þorskur shoal runs 68–92 cm. 🎯 **He looked at
   * exactly that and said *"all the þorskur are the same size"***, which is a
   * measurement of how much spread it takes to read through water: **15% does
   * not.** That is why these three are far apart rather than a little apart.
   */
  readonly length: number;
  /**
   * 🔴 **Is it BUILT yet? ALL FOUR ARE, since 2026-09-18.**
   *
   * ⚠️ **Not a filter written at the call site.** It exists because a fish can
   * be designed long before it has anywhere to be, and **the reason has to be
   * written where somebody will read it, or the next session simply switches it
   * on.** 🔴 **The reason was never technical: the nearest river is 6.9 km from
   * where anybody plays**, so a river-only fish would be correct and
   * unmeetable — T223.
   *
   * ✅ **LAX WAS SWITCHED ON, 2026-09-17, AND IT IS NOT A CHEAT.** 🎯 haugur:
   * *"we still have to create 2 more fish for the ocean… Lax and Ufsi."*
   * 🔴 **An Atlantic salmon lives at SEA and only enters a river to spawn** —
   * so putting it in the sea is what the real fish does, and it is exactly what
   * removes the objection above: **in the sea it can be met.** ⚠️ Its `water`
   * is `both`, unchanged, and the river half still has nowhere to happen.
   *
   * ⚠️ **THIS FLAG SAID `true` FOR THE UFSI WHILE NOTHING SPAWNED ONE**, from
   * the afternoon to the evening of 2026-09-17, and a test asserted it. **A data file claiming a
   * feature exists is the same fault as a comment claiming it** — see the
   * `players` table, whose comment described a writer nobody had built.
   */
  readonly built: boolean;
}

/** 🐟 The four, in the order haugur listed them. */
export const FISH: readonly FishSpecies[] = Object.freeze([
  Object.freeze({
    key: 'thorskur' as const,
    name: 'Cod',
    nameIs: 'Þorskur',
    water: 'sea' as const,
    food: 2,
    length: 0.8,
    built: true,
  }),
  Object.freeze({
    key: 'ufsi' as const,
    name: 'Saithe',
    nameIs: 'Ufsi',
    water: 'sea' as const,
    food: 1,
    /* 80% of the þorskur — haugur, 2026-09-17. See {@link FishSpecies.length}. */
    length: 0.64,
    built: true,
  }),
  Object.freeze({
    key: 'lax' as const,
    name: 'Salmon',
    nameIs: 'Lax',
    water: 'both' as const,
    food: 3,
    /* 135% of the þorskur — haugur, 2026-09-17. See {@link FishSpecies.length}. */
    length: 1.08,
    built: true,
  }),
  Object.freeze({
    key: 'bleikja' as const,
    name: 'Arctic char',
    nameIs: 'Bleikja',
    /**
     * 🐠 **`both`, and a SEA fish here — because a sjóbleikja is one** (T232).
     *
     * 🔴 **Sea-run Arctic char go to sea for about FIFTY DAYS each summer**,
     * roughly mid-June to mid-July, and then run back up the river. **So the
     * fourth fish genuinely belongs in the sea for part of the year.**
     *
     * ⚠️ **This is the lax's argument exactly, and it is the same argument.**
     * D115 held both back as *the river two* because **the nearest watercourse
     * is 6.9 km from where anybody plays** — and that reason was always about
     * the WATER, never about the fish. In the sea it can be met.
     *
     * ⬜ **The seasonality is NOT built.** A bleikja is in the sea all year
     * here, which is wrong about the real fish and honest about the code.
     * 👉 **It is a real detail waiting to be used** rather than a fudge to be
     * undone, and T234 holds it.
     */
    water: 'both' as const,
    food: 1,
    /**
     * 📏 **60% of the þorskur — 48 cm.**
     *
     * ⚠️ **A PROPOSAL, and haugur set the other three himself** (ufsi 80%, lax
     * 135%). 📏 **A real Icelandic sjóbleikja runs 40–60 cm**, so this is an
     * ordinary adult rather than a trophy — unlike our lax, which at 1.08 m
     * deliberately is one.
     *
     * ✅ **It makes the bleikja the smallest fish in the water**, which widens
     * the size spread he asked for: 🎯 *"that way we get diversity to the look
     * of the fish schools."*
     *
     * 🔴 **IT WAS 70% — 56 cm — FOR ABOUT AN HOUR, AND A TEST THREW IT OUT.**
     * ⚠️ **56 cm is only 14% under the ufsi's 64**, and the client already
     * draws every fish at **±15% of its species length** from its own id — so
     * **the two would have overlapped completely and read as one fish.**
     * 🎯 That is haugur's own measurement talking: *"all the þorskur are the
     * same size."* ✅ **At 48 cm every step in the sea is 1.25× to 1.35×.**
     * 👉 **The test was written for three fish and caught the fourth by
     * itself**, which is what an invariant is for.
     */
    length: 0.48,
    /**
     * ✅ **BUILT, 2026-09-18 — and it is haugur's thickness experiment** (T232).
     * 🎯 *"i want to create bleikja as a 3d model and put it into the ocean to
     * see the differrence."*
     *
     * 🔴 **It is the only fish in the game with a SOLID BODY** —
     * `client/src/fishbody.ts` — where the other three are photographs on two
     * flat cards crossed in an X. ⚠️ **Everything else about it is identical on
     * purpose**: same spine, same behaviours, same wave, same water, same shoal
     * budget, **and exactly the same width**. *The experiment has one variable.*
     */
    built: true,
  }),
]);

/** Look one up. `undefined` rather than a throw — a saved key may outlive a rename. */
export function fishByKey(key: string): FishSpecies | undefined {
  return FISH.find((f) => f.key === key);
}

/**
 * 🍲 **What one *food* restores, as a share of the bar** — D115.
 *
 * ⚠️ **Raw and cooked are the SAME NUMBER doubled**, written once here rather
 * than as two tables that can disagree. 🔴 **Cooking does not exist yet (T222)**,
 * so nothing calls the cooked side in anger — and the research says Minecraft
 * never multiplies a COUNT, one in and one out every time, which is what haugur
 * was told before he chose value over pieces.
 */
export const FOOD_UNIT_RAW_PERCENT = 12.5;
export const FOOD_UNIT_COOKED_PERCENT = FOOD_UNIT_RAW_PERCENT * 2;

/** How much of the food bar this fish restores. */
export function restoresPercent(species: FishSpecies, cooked: boolean): number {
  return species.food * (cooked ? FOOD_UNIT_COOKED_PERCENT : FOOD_UNIT_RAW_PERCENT);
}

/**
 * 🔴 **EVERYTHING BELOW THIS LINE WAS DELETED ON 2026-09-17 — T228, D117.**
 *
 * **What went:** `fishAt`, its hash, and every number that fed it —
 * `FISH_PER_SQUARE_METRE`, `FISH_SHORE_DEPTH`, `shoreFactor`,
 * `FISH_MIN_DEPTH`, `FISH_MAX_DEPTH`, `FISH_ABOVE_BED`, `FISH_SPEED`.
 *
 * 🎯 **haugur, having watched what they produced:** *"i just realised what our
 * current cod is, its a xerox copy of a picture of a cod floting in a sea that
 * has no movement."*
 *
 * ⚠️ **It was not a bug. It was the design.** A fish's travel was a **sawtooth**
 * — eight metres in a dead straight line and then **teleport back to the
 * start**, every 11 to 22 seconds. *"The next one came"* was the same fish, back
 * at point A.
 *
 * 🔴 **And the fault it could never have escaped:** the player's position is not
 * in a formula, so **a derived fish cannot notice you** (D117). 🎯 *"yes animals
 * should notice you, thats important."*
 *
 * ✅ **What lives where now:**
 * - **The mind** → [`fishmind.ts`](fishmind.ts), a {@link Species} with three
 *   named behaviours.
 * - **The population** → `server/src/shoal.ts`, a budget near each player.
 * - **The body** → `client/src/fishmesh.ts`, **unchanged**: the same
 *   photograph and the same bending tail — ⚠️ **but NOT the same shape.** 🆕
 *   **Every fish is a SOLID BODY since 2026-09-18** (T232, D119): a lofted hull
 *   wearing the photograph, with a flat sheet for the tail fin. **The crossed
 *   card is retired from the sea** and survives in `fishgeom.ts` only as the
 *   shape a fish would go back to.
 *
 * ⚠️ **WHAT STAYED IS ABOVE: the four names, their food values and their
 * lengths.** Those are D115 and they are haugur's; nothing about them was
 * wrong.
 *
 * 👉 **If you are about to re-derive a creature to save a message, read
 * [`Development/14_The_Animal_System.md`](../../../Development/14_The_Animal_System.md)
 * first. It will look sensible. It looked sensible last time.**
 */
